import { useCallback, useMemo, useState } from 'react';

import type { CompanyGroupDetailResponse, ContractResponse } from '../../api';
import {
  AsyncSection,
  EmptyState,
  Pagination,
  Panel,
  usePagination,
  type ColumnFiltersState,
  type SortingState,
} from '../../components';
import { formatDate, todayInAppTimeZone } from '../../format';
import { staffCompanyPersonPath } from '../../routes/paths';
import { useGroupRoster } from '../queries';
import { SeguradosTable, type SeguradoRow } from './SeguradosTable';
import { GROUPS_COPY } from './groups';
import { coveredCount, entryLabel } from './segurados';
import {
  SEGURADOS_PAGE_SIZE,
  contractsFilterOptions,
  entriesInView,
  filterSegurados,
  isNarrowed,
  seguradosFilterFrom,
  seguradosFootnote,
  sortSegurados,
} from './seguradosFilters';

/**
 * No contract listing is read at group level -- that would be one request per
 * company -- so a contract's tag is its kind alone, which is what tells a
 * person's contracts apart, and the Contratos filter offers kinds only. The
 * Empresa column says whose each one is.
 */
const NO_CONTRACTS: ReadonlyMap<string, ContractResponse> = new Map();
const CONTRACT_OPTIONS = contractsFilterOptions();

/**
 * Who is insured today under any contract of any company in this group, for
 * staff answering "is this person registered, and under which company?" on
 * one screen rather than one company page at a time.
 *
 * Read-only, and one request: the group's population (#416), one item per
 * CPF with an entry per covering contract, each naming its company. The
 * table's filter row -- name, CPF, a kind, one member company -- its sortable
 * headers and the paging all work on what has already arrived
 * (`seguradosFilters`), so no partial CPF reaches a URL.
 *
 * The table is the company page's own (`SeguradosTable`), plus an Empresa
 * column. It leaves out the scheduled-change markers: those come from one
 * read per contract, which is the fan-out this panel exists to avoid, and the
 * description sends the reader to each company's page for them -- and, for the
 * same reason, there is no "Movimentação agendada" filter here.
 */
export function GroupSeguradosPanel({ group }: { group: CompanyGroupDetailResponse }) {
  const [asOf] = useState(todayInAppTimeZone);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const roster = useGroupRoster(group.id, asOf, group.members.length > 0);

  const companies = useMemo(
    () => new Map(group.members.map((member) => [member.id, member.name])),
    [group.members],
  );

  const filter = useMemo(() => seguradosFilterFrom(columnFilters), [columnFilters]);
  const inView = useCallback((person: SeguradoRow) => entriesInView(person, filter), [filter]);
  const people = useMemo(
    () => sortSegurados(filterSegurados(roster.data?.items ?? [], filter), sorting, inView),
    [roster.data, filter, sorting, inView],
  );
  const pagination = usePagination(people, SEGURADOS_PAGE_SIZE);

  const position = `Posição em ${formatDate(asOf)}.`;
  const description =
    roster.data === undefined
      ? `${GROUPS_COPY.seguradosDescription} ${position}`
      : `${GROUPS_COPY.seguradosDescription} ${position} ${coveredCount(roster.data.count)}.`;

  return (
    <Panel title={GROUPS_COPY.seguradosTitle} description={description}>
      {group.members.length === 0 ? (
        <EmptyState
          title={GROUPS_COPY.seguradosNoMembers}
          description={GROUPS_COPY.seguradosNoMembersDescription}
        />
      ) : (
        <AsyncSection
          state={roster}
          loadingLabel={GROUPS_COPY.seguradosLoading}
          errorTitle={GROUPS_COPY.seguradosError}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={GROUPS_COPY.seguradosEmpty}
          emptyDescription={GROUPS_COPY.seguradosEmptyDescription}
        >
          {(data) => (
            <>
              <SeguradosTable
                id="group-segurados"
                caption={GROUPS_COPY.seguradosCaption}
                people={pagination.items}
                entriesInView={inView}
                // Any change to what is filtered or how it is sorted starts
                // again from page 1.
                columnFilters={columnFilters}
                onColumnFiltersChange={(next) => {
                  setColumnFilters(next);
                  pagination.setPage(1);
                }}
                sorting={sorting}
                onSortingChange={(next) => {
                  setSorting(next);
                  pagination.setPage(1);
                }}
                contractOptions={CONTRACT_OPTIONS}
                // Each entry names its company, so the person page resolves
                // from the row alone: the first entry's company and id.
                personPath={(entry) =>
                  staffCompanyPersonPath(entry.company_id ?? '', entry.insured_person_id)
                }
                contractLabel={(entry) => entryLabel(entry, NO_CONTRACTS)}
                companies={companies}
                noMatchesHint={GROUPS_COPY.seguradosNoMatchesHint}
              />
              <Pagination
                page={pagination.page}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onPageChange={pagination.setPage}
              />
              <p className="panel__footnote">
                {seguradosFootnote({
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  matching: people.length,
                  total: data.count,
                  narrowed: isNarrowed(filter),
                })}
              </p>
            </>
          )}
        </AsyncSection>
      )}
    </Panel>
  );
}
